import React, { useState, forwardRef, useImperativeHandle, useRef } from "react";
import { Grid, Paper, Button, Box, IconButton } from "@mui/material";
import DynamicForm from "shared/organisms/dynamicform";
import useReactHookForm from "hooks/useReactHookForm";
import ContentRight from "shared/molecules/content-right";
import { hasAtLeastOneValue, ObserverInitialValues, ObserverProperties, ObserverValidationSchema } from "./helper";
import { formatDateUtc } from "utils/time-util/utcIstTime";
import { useHistory, useLocation } from "react-router-dom";
import { useMutation } from "@apollo/client";
import { CREATE_NLEARN3_PACKAGE_SCHEDULE_OBSERVER } from "utils/graphql/services/live-classes/mutations";
import liveClassClientService from "utils/graphql/liveclassClient";
import { TypographyHeading } from "shared/molecules/typography-layout";
import { snackbarRequest } from "shared/molecules/snackbar/snackbar-slice";
import { useDispatch } from "react-redux";
import LoadingButton from "@mui/lab/LoadingButton";

const ObserverFormRecord = forwardRef(({ onRemove, showRemove, onStatusChange, projectId, isSubmitting}: any, ref) => {
    const { control, errors, getValues, watch, setValue } = useReactHookForm(
        ObserverInitialValues,
        ObserverValidationSchema,
        ""
    );

    const watchValues = watch();

    React.useEffect(() => {
        const isFilled = Object.values(watchValues).every(val => !!val);
        onStatusChange(isFilled);
    }, [JSON.stringify(watchValues), onStatusChange]);

    useImperativeHandle(ref, () => ({
        getValues: () => {
            const payload: any = {};

            const vendorId = watch("vendorId");
            if (vendorId) payload.vendorId = parseInt(vendorId);

            const vendorAccountId = watch("vendorAccountId");
            if (vendorAccountId) payload.vendorAccountId = parseInt(vendorAccountId);

            const teacherId = watch("teacherId");
            if (teacherId) payload.teacherId = parseInt(teacherId);

            const vendorUserId = watch("vendorUserId");
            if (vendorUserId) payload.vendorUserId = parseInt(vendorUserId);
            return payload;
        },

        isValid: () => {
            return Object.keys(errors).length === 0;
        }
    }));

    const clearFilter = () => {
        setValue("vendorId", "");
        setValue("vendorAccountId", "");
        setValue("departmentId", "");
        setValue("designationId", "");
        setValue("teacherId", "");
        setValue("vendorUserId", "");
    };

    return (
        <Paper elevation={2} sx={{ px: 2, pb: 3, pt: 3, mb: 2, position: "relative" }}>
            <Grid container spacing={2}>
                <DynamicForm
                    fields={ObserverProperties(projectId)}
                    control={control}
                    errors={errors}
                    watch={watch}
                    setValue={setValue}
                    getValues={getValues}
                />
                <ContentRight sx={{ mt: -7, width: '100%' }}>
                    {showRemove && (
                        <Button
                            variant="outlined"
                            sx={{
                                color: "red",
                                marginTop: "15px",
                                marginRight: "10px",
                                height: "40px",
                            }}
                            onClick={onRemove}
                            disabled={isSubmitting}
                        >
                            Delete
                        </Button>
                    )}
                    <Button
                        variant="contained"
                        sx={{
                            width: "70px",
                            height: "40px",
                            marginTop: "15px",
                            marginRight: "10px",
                            padding: "7px 42.5px 7px 48.5px",
                            cursor: "pointer",
                            textAlign: "center",
                        }}
                        onClick={clearFilter}
                        disabled={!hasAtLeastOneValue(watch(), ["vendorId", "vendorAccountId", "departmentId", "designationId", "teacherId", "vendorUserId"]) || Object.keys(errors).length > 0 || isSubmitting}
                    >
                        clear
                    </Button>
                </ContentRight>
            </Grid>
        </Paper>
    );
});

export function ObserverCreate({ setSearchPayload, setPage }: any) {
    const [records, setRecords] = useState([{ id: Date.now(), isFilled: false }]);
    const refs = useRef<{ [key: number]: any }>({});
    const history = useHistory();
    const dispatch = useDispatch();
    const location: any = useLocation();
    const row = location?.state?.row;
    const [isSubmitting, setIsSubmitting]= useState(false);

    const [createObserver, { loading }] = useMutation(
        CREATE_NLEARN3_PACKAGE_SCHEDULE_OBSERVER,
        {
            client: liveClassClientService,
        }
    );

    const handleAddMore = () => {
        setRecords([...records, { id: Date.now(), isFilled: false }]);
    };

    const handleStatusChange = React.useCallback((id: number, isFilled: boolean) => {
        setRecords((prev) => {
            const existing = prev.find((r) => r.id === id);
            if (existing?.isFilled === isFilled) return prev;
            return prev.map((r) => (r.id === id ? { ...r, isFilled } : r));
        });
    }, []);

    const handleRemove = (id: number) => {
        setRecords(records.filter((r) => r.id !== id));
        delete refs.current[id];
    };

    const handleSubmitAll = async () => {
        // Collect all valid objects
        if(isSubmitting) return;
        setIsSubmitting(true);

        const isValidForm = records.every((record) => {
            const ref = refs.current[record.id];
            return ref?.isValid();
        });

        if (!isValidForm) {
            dispatch(snackbarRequest({ message: "Please check all fields and ensure there are no errors.", type: "error" }));
            return;
        }


        const allPayloads = records.map((record) => {
            const payload = refs.current[record.id]?.getValues();
            return {
                ...payload,
                liveSessionId: row?.liveSessionId ? Number(row.liveSessionId) : null,
                projectId: row?.liveSession?.projectId ? Number(row.liveSession.projectId) : null,
            }
        });

        try {
            const response = await createObserver({
                variables: {
                    input: allPayloads,
                },
            });

            if (response?.data?.createNlearn3PackageScheduleObserver?.message) {
                dispatch(
                    snackbarRequest({
                        message: response.data.createNlearn3PackageScheduleObserver.message,
                        type: "success",
                    })
                );
                history.goBack();
            }
        } catch (error: any) {
            console.error("GraphQL error", error);

            const errorMessage =
                error?.graphQLErrors?.[0]?.extensions?.message ||
                error?.graphQLErrors?.[0]?.message ||
                error?.message ||
                "Failed to create observer";

            dispatch(
                snackbarRequest({
                    message: errorMessage,
                    type: "error",
                })
            );
        } finally{
            setIsSubmitting(false)
        }
    };

    return (
        <>
            <TypographyHeading sx={{ mb: 2, mt: 3, fontSize: "25px" }}>
                Create Observer
            </TypographyHeading>

            <Box sx={{ mt: 3, mb: 3 }}>
                {records.map((record) => (
                    <ObserverFormRecord
                        key={record.id}
                        ref={(el: any) => (refs.current[record.id] = el)}
                        onRemove={() => handleRemove(record.id)}
                        showRemove={records.length > 1}
                        onStatusChange={(status: boolean) => handleStatusChange(record.id, status)}
                        projectId ={row?.liveSession?.projectId}
                        isSubmitting={isSubmitting}
                    />
                ))}

                <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
                    <Button
                        variant="outlined"
                        color="primary"
                        onClick={handleAddMore}
                        disabled={!records[records.length - 1]?.isFilled || isSubmitting}
                    >
                        Add More
                    </Button>
                    <LoadingButton
                        variant="contained"
                        color="primary"
                        onClick={handleSubmitAll}
                        disabled={records.some(r => !r.isFilled) || isSubmitting}
                        loading={isSubmitting}
                    >
                        Submit
                    </LoadingButton>
                </Box>
            </Box>
        </>
    );
}
